// @ts-nocheck
import React from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import PropTypes from 'prop-types';

import {
  MentionComponent,
  ModelMentionComponent,
  ArtifactMentionComponent,
  DocumentMentionComponent,
  AttributeMentionComponent,
} from '../MentionComponents';
import { useMentionContext } from '../MentionContext';

/**
 * Empty child placeholder passed to legacy mention chip components.
 *
 * The original Draft.js mention components expect a `children` prop from the
 * decorator API. Tiptap node views do not provide decorator children, so this
 * stable empty span preserves the legacy component contract without rendering
 * extra visible content.
 *
 * @type {JSX.Element}
 */
const EMPTY_SPAN = <span />;

/**
 * Renders a saved `@user` mention inside Tiptap content.
 *
 * Tiptap stores the mention payload on `node.attrs`; the legacy
 * MentionComponent expects `decoratedText` plus a small `mention` object. This
 * node view adapts the persisted attrs so saved user mentions keep the same
 * non-editable chip UI after the Draft.js to Tiptap migration.
 *
 * @param {object} props - Tiptap React node-view props.
 * @param {import('@tiptap/pm/model').Node} props.node - Tiptap user mention node.
 * @param {object} props.node.attrs - User mention attrs persisted in Tiptap JSON.
 * @param {string} props.node.attrs.label - Display label inserted after the `@` trigger.
 * @param {string} props.node.attrs.name - User name used by the existing Draft mention chip.
 * @param {string} props.node.attrs.designation - Optional user designation shown in the chip tooltip.
 * @returns {JSX.Element} Non-editable user mention chip.
 */
export const UserMentionNodeView = ({ node }) => (
  <NodeViewWrapper as="span" contentEditable={false}>
    <MentionComponent
      decoratedText={`@${node.attrs.label}`}
      mention={{
        name: node.attrs.name || node.attrs.label,
        designation: node.attrs.designation || '',
      }}
    >
      {EMPTY_SPAN}
    </MentionComponent>
  </NodeViewWrapper>
);
UserMentionNodeView.propTypes = {
  node: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

/**
 * Renders a saved `model:` mention using the existing model mention chip UI.
 *
 * The model mention extension persists model search result fields in Tiptap
 * attrs. This adapter maps those attrs into the backend-style keys consumed by
 * ModelMentionComponent so saved model mentions retain their previous display
 * and open/click behavior.
 *
 * @param {object} props - Tiptap React node-view props.
 * @param {import('@tiptap/pm/model').Node} props.node - Tiptap model mention node.
 * @param {object} props.node.attrs - Model mention attrs persisted in Tiptap JSON.
 * @param {string} props.node.attrs.uniqueId - Model unique id shown before the name.
 * @param {string} props.node.attrs.name - Model display name.
 * @param {string} props.node.attrs.entityType - Entity type needed by click/open behavior.
 * @param {string} props.node.attrs.requestType - Optional request type carried from mention search.
 * @returns {JSX.Element} Non-editable model mention chip.
 */
export const ModelMentionNodeView = ({ node }) => (
  <NodeViewWrapper as="span" contentEditable={false}>
    <ModelMentionComponent
      mention={{
        Unique_id: node.attrs.uniqueId,
        Name: node.attrs.name,
        Entity_type: node.attrs.entityType,
        request_type: node.attrs.requestType || '',
      }}
    >
      {EMPTY_SPAN}
    </ModelMentionComponent>
  </NodeViewWrapper>
);
ModelMentionNodeView.propTypes = {
  node: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

/**
 * Renders a saved `artifact:` mention using the existing artifact mention chip UI.
 *
 * Artifact mentions share the same persisted attr shape as model mentions, but
 * route through ArtifactMentionComponent for the artifact-specific chip and
 * click behavior. This keeps saved `artifact:` mentions compatible with the
 * legacy UI contract while being rendered by Tiptap.
 *
 * @param {object} props - Tiptap React node-view props.
 * @param {import('@tiptap/pm/model').Node} props.node - Tiptap artifact mention node.
 * @param {object} props.node.attrs - Artifact mention attrs persisted in Tiptap JSON.
 * @param {string} props.node.attrs.uniqueId - Artifact unique id shown before the name.
 * @param {string} props.node.attrs.name - Artifact display name.
 * @param {string} props.node.attrs.entityType - Entity type needed by click/open behavior.
 * @param {string} props.node.attrs.requestType - Optional request type carried from mention search.
 * @returns {JSX.Element} Non-editable artifact mention chip.
 */
export const ArtifactMentionNodeView = ({ node }) => (
  <NodeViewWrapper as="span" contentEditable={false}>
    <ArtifactMentionComponent
      mention={{
        Unique_id: node.attrs.uniqueId,
        Name: node.attrs.name,
        Entity_type: node.attrs.entityType,
        request_type: node.attrs.requestType || '',
      }}
    >
      {EMPTY_SPAN}
    </ArtifactMentionComponent>
  </NodeViewWrapper>
);
ArtifactMentionNodeView.propTypes = {
  node: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

/**
 * Renders a saved `document:` mention and wires the document preview/share helpers from context.
 *
 * Tiptap stores document mention fields as attrs, while
 * DocumentMentionComponent expects a nested `props.mention` object with legacy
 * backend-style keys. This node view adapts the attrs and injects
 * `fetchDocumentDetails` / `fetchSharableLink` from MentionContext so saved
 * document mentions keep their existing preview/share behavior.
 *
 * @param {object} props - Tiptap React node-view props.
 * @param {import('@tiptap/pm/model').Node} props.node - Tiptap document mention node.
 * @param {object} props.node.attrs - Document mention attrs persisted in Tiptap JSON.
 * @param {string} props.node.attrs.name - Document display name.
 * @param {number|string} props.node.attrs.entityId - Document id used by preview/open behavior.
 * @param {string} props.node.attrs.entityType - Document entity type.
 * @param {number|string} props.node.attrs.parentEntityId - Parent entity id used for document APIs.
 * @returns {JSX.Element} Non-editable document mention chip.
 */
export const DocumentMentionNodeView = ({ node }) => {
  const { fetchDocumentDetails, fetchSharableLink } = useMentionContext();
  return (
    <NodeViewWrapper as="span" contentEditable={false}>
      <DocumentMentionComponent
        fetchDocumentDetails={fetchDocumentDetails}
        fetchSharableLink={fetchSharableLink}
        props={{
          mention: {
            Name: node.attrs.name,
            Id: node.attrs.entityId,
            Entity_type: node.attrs.entityType,
            Entity_id: node.attrs.parentEntityId,
          },
          children: EMPTY_SPAN,
        }}
      />
    </NodeViewWrapper>
  );
};

DocumentMentionNodeView.propTypes = {
  node: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

/**
 * Renders a saved `attribute:` mention and wires the attribute popover helper from context.
 *
 * Tiptap stores mention data in `node.attrs`, while the legacy
 * AttributeMentionComponent expects a `mention` object with backend-style keys.
 * This node view adapts the Tiptap attrs into that legacy shape so saved
 * `attribute:` mentions can still use the existing chip and popover behavior.
 *
 * `Entity_id` intentionally comes from `parentEntityId`, not the attribute's
 * own id, because the attribute detail API needs the parent model/entity id
 * when the chip is clicked. `getAttributeDetails` is read from MentionContext
 * and passed through unchanged to preserve the existing lookup flow.
 *
 * @param {object} props - Tiptap React node-view props.
 * @param {import('@tiptap/pm/model').Node} props.node - Tiptap attribute mention node.
 * @param {object} props.node.attrs - Attribute mention attrs persisted in Tiptap JSON.
 * @param {string} props.node.attrs.name - Attribute display name.
 * @param {number|string} props.node.attrs.entityId - Attribute id mapped to legacy `Id`.
 * @param {string} props.node.attrs.entityType - Parent entity type.
 * @param {number|string} props.node.attrs.parentEntityId - Parent model/entity id mapped to legacy `Entity_id`.
 * @returns {JSX.Element} Non-editable attribute mention chip.
 */
export const AttributeMentionNodeView = ({ node }) => {
  const { getAttributeDetails } = useMentionContext();
  return (
    <NodeViewWrapper as="span" contentEditable={false}>
      <AttributeMentionComponent
        getAttributeDetails={getAttributeDetails}
        props={{
          mention: {
            Name: node.attrs.name,
            Id: node.attrs.entityId,
            Entity_type: node.attrs.entityType,
            Entity_id: node.attrs.parentEntityId,
          },
          children: EMPTY_SPAN,
        }}
      />
    </NodeViewWrapper>
  );
};

AttributeMentionNodeView.propTypes = {
  node: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
